import { useEffect, useState } from "react";
import { canInstall, onInstallChange, promptInstall, isStandalone } from "@/lib/pwa";
import { DownloadCloud, Smartphone, X } from "lucide-react";
import { useTranslation } from "react-i18next";

/** Floating "Install App" button. Appears when the browser announces install-ability
 *  and auto-hides once the app is installed / dismissed for the session. */
export default function InstallAppButton() {
  const { i18n } = useTranslation();
  const [installable, setInstallable] = useState(canInstall());
  const [installed, setInstalled] = useState(isStandalone());
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("iqes_install_dismissed") === "1");

  useEffect(() => {
    const unsub = onInstallChange(setInstallable);
    return () => unsub();
  }, []);

  if (installed || !installable || dismissed) return null;

  const es = i18n.language?.startsWith("es");

  return (
    <div
      data-testid="install-app-banner"
      className="fixed bottom-6 left-6 z-40 max-w-sm bg-white border border-[#003366]/20 rounded-xl shadow-lg p-3 flex items-start gap-3"
    >
      <div className="w-9 h-9 rounded-xl bg-[#003366] text-white flex items-center justify-center shrink-0">
        <Smartphone size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900">
          {es ? "Instala IQES IQ-APP" : "Install IQES IQ-APP"}
        </div>
        <div className="text-xs text-slate-600 mt-0.5">
          {es ? "Úsala como app nativa en tu PC o móvil." : "Use it as a native app on PC or mobile."}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <button
            data-testid="install-app-btn"
            onClick={async () => {
              const res = await promptInstall();
              if (res?.outcome !== "accepted") {
                setDismissed(true);
                sessionStorage.setItem("iqes_install_dismissed", "1");
              }
            }}
            className="bg-[#EA580C] text-white hover:bg-[#C2410C] rounded-xl px-3 py-1.5 font-medium text-xs transition-colors inline-flex items-center gap-1.5"
          >
            <DownloadCloud size={12} />
            {es ? "Instalar" : "Install"}
          </button>
          <button
            data-testid="install-dismiss-btn"
            onClick={() => { setDismissed(true); sessionStorage.setItem("iqes_install_dismissed", "1"); }}
            className="text-xs font-mono uppercase tracking-wider text-slate-500 hover:text-slate-900 px-2"
          >
            {es ? "Después" : "Later"}
          </button>
        </div>
      </div>
      <button
        onClick={() => { setDismissed(true); sessionStorage.setItem("iqes_install_dismissed", "1"); }}
        className="text-slate-400 hover:text-slate-900 shrink-0"
      >
        <X size={14} />
      </button>
    </div>
  );
}
